import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { injectQuery, injectQueryClient } from '@ngneat/query'
import { Observable, combineLatest, map } from 'rxjs'
import { EnvironmentOverview } from './environment-overview.model'
import { ATLAS_ENVIRONMENTS, AtlasEnvironment } from './environments'
import { queryKeys } from './query-keys'

export type ConnectionState = 'checking' | 'connected' | 'signInRequired' | 'notPermitted' | 'unreachable'

export type EnvironmentStatus = {
  environment: AtlasEnvironment
  state: ConnectionState
  overview: EnvironmentOverview | null
  checkedAt: Date | null
}

function stateFromError(error: unknown): ConnectionState {
  if (!(error instanceof HttpErrorResponse)) {
    return 'unreachable'
  }
  if (error.status === 401) {
    return 'signInRequired'
  }
  if (error.status === 403) {
    return 'notPermitted'
  }
  // Status 0 covers both a dead host and a response the browser refused to expose, which is
  // what an environment that has not deployed the endpoint yet looks like from here.
  return 'unreachable'
}

@Injectable({ providedIn: 'root' })
export class EnvironmentStatusService {
  private http = inject(HttpClient)
  private query = injectQuery()
  private queryClient = injectQueryClient()

  readonly statuses$: Observable<EnvironmentStatus[]> = combineLatest(ATLAS_ENVIRONMENTS.map((environment) => this.watch(environment)))

  refresh(environment: AtlasEnvironment): void {
    void this.queryClient.refetchQueries({ queryKey: queryKeys.environmentOverview(environment.key) })
  }

  refreshAll(): void {
    for (const environment of ATLAS_ENVIRONMENTS) {
      this.refresh(environment)
    }
  }

  private watch(environment: AtlasEnvironment): Observable<EnvironmentStatus> {
    return this.query({
      queryKey: queryKeys.environmentOverview(environment.key),
      queryFn: () => this.http.get<EnvironmentOverview>(`https://${environment.host}/api/deployment-overview`, { withCredentials: true }),
      // A 401 here is an ordinary state Atlas renders a button for, not a fault worth retrying,
      // and refetching on focus is what closes the loop after signing in on the environment's
      // own tab.
      retry: 0,
      staleTime: 0,
      refetchOnWindowFocus: true,
    }).result$.pipe(
      map((result) => {
        const checkedAt = Math.max(result.dataUpdatedAt, result.errorUpdatedAt)
        return {
          environment,
          state: this.stateFromResult(result),
          overview: result.data ?? null,
          checkedAt: checkedAt === 0 ? null : new Date(checkedAt),
        }
      })
    )
  }

  private stateFromResult(result: { isSuccess: boolean; isError: boolean; error: unknown }): ConnectionState {
    if (result.isSuccess) {
      return 'connected'
    }
    if (result.isError) {
      return stateFromError(result.error)
    }
    return 'checking'
  }
}
